// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useState } from 'react';

import type { Submission, SubmissionPart, SubmissionState, SubmissionTarget } from '../../../bygning/submission';
import { Pill } from '../../Pill';
import styles from './RapportList.module.css';

const MARK: Record<SubmissionState, string> = { ok: '✓', no: '!', unknown: '?', external: '–' };
const STATE_TEXT: Record<SubmissionState, string> = {
  ok: 'opfyldt',
  no: 'ikke opfyldt',
  unknown: 'ukendt',
  external: 'udfyldes uden for ReUseX',
};

export interface SubmissionChecklistProps {
  submission: Submission;
  /** Follows a row: a selection, or a place in Rapport's right tile. */
  onGo: (target: SubmissionTarget) => void;
}

/**
 * "Til bygningsaffald.dk" (prototype Rapport › Del 1 / Del 2): the two parts
 * the case is submitted in, each a list of what goes in with a ✓/!/?/– mark,
 * a subline with the figures and a pill, each row a jump to where it is
 * fixed. Each part folds (`<details>`) to keep the right tile short: the
 * next one to submit is open, the other closed until the user opens it.
 * What the rows say is `bygning/submission.ts`.
 */
export function SubmissionChecklist({ submission, onGo }: SubmissionChecklistProps) {
  return (
    <>
      {submission.parts.map((p) => (
        <Part key={p.part} part={p} next={submission.next.part === p.part} onGo={onGo} />
      ))}
    </>
  );
}

function Part({ part, next, onGo }: { part: SubmissionPart; next: boolean; onGo: SubmissionChecklistProps['onGo'] }) {
  const id = useId();
  // Open while it is the next part; the user's own toggle holds until the next part changes.
  const [open, setOpen] = useState(next);
  useEffect(() => setOpen(next), [next]);
  return (
    <details className={styles.part} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className={styles.summary}>
        <span className={styles.head}>
          <h3 id={`${id}-h`} className={styles.heading}>
            {part.title}
          </h3>
          {part.missing > 0 ? <Pill tone="warn">{part.missing} mangler</Pill> : <Pill tone="good">Klar</Pill>}
        </span>
        <span className={part.subTone ? `${styles.sub} ${styles[`sub_${part.subTone}`]}` : styles.sub}>{part.sub}</span>
      </summary>
      <ul className={styles.list} aria-labelledby={`${id}-h`}>
        {part.rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              className={styles.row}
              onClick={() => onGo(r.target)}
              aria-label={`${r.title} — ${STATE_TEXT[r.state]}, ${r.detail}, ${r.pill.text}`}
            >
              <i className={`${styles.mark} ${styles[r.state]}`} aria-hidden="true">
                {MARK[r.state]}
              </i>
              <span className={styles.text}>
                <span className={styles.title}>{r.title}</span>
                <small className={styles.detail}>{r.detail}</small>
              </span>
              <span className={styles.pills} aria-hidden="true">
                <Pill tone={r.pill.tone} variant={r.pill.outline ? 'outline' : undefined}>
                  {r.pill.text}
                </Pill>
                <span className={styles.go}>→</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
